import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useFitHeight } from '../lib/fitHeight';
import { TopScrollbar } from '../components/TopScrollbar';
import { api, type Topline, type ToplineBucket, type ToplineLine, type ToplinePayee } from '../lib/api';
import { ErrorBox, commaInput, dash, money, uncomma } from '../components/DataTable';
import Modal from '../components/Modal';
import { SalarySlip } from './Settle';
import { canScreen, useMe } from '../lib/me';
import { useSticky } from '../lib/sticky';
import { hitAny, searchWords } from '../lib/search';

/**
 * 차인지급액 조정(「탑라인」) — 회계팀 월별 엑셀 「월별통합(법인별)」을 한 화면으로(2026-09-28 회의).
 * 서버 `functions/src/topline.ts` · 계산 규칙 `toplineCalc.ts` · 기획 `plan/topline.md`.
 *
 * 한 줄 목표: **법인별로, 누구에게, 얼마를 줄 것인가.** 엑셀의 시트 나누기·색칠을 「구분」 칩 필터로 바꿨다.
 * 🔴 화면 낱말: 「차주」·「직영」·「지입」은 쓰지 않는다 → 관리기사A/B · 명의(저장값·서버 키는 원문 그대로).
 */

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const prevMonth = () => {
  const d = new Date(Date.now() + 9 * 3600_000);
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
};
const BUCKET_LABEL: Record<ToplineBucket, string> = {
  지입기본: '관리기사B', 사업소득: '사업소득(3.3%)', '2대이상': '2대 이상', 직영: '관리기사A', 예비기사: '예비기사', '25일': '25일 지급',
};
const SUMMARY_COLS: ToplineBucket[] = ['지입기본', '사업소득', '2대이상', '직영', '예비기사'];
const TAG_LABEL: Record<string, string> = { 직영: '관리기사A' };
const tagLabel = (t: string) => TAG_LABEL[t] ?? t;
const TAG_STYLE: Record<string, string> = {
  마이너스: 'bg-red-50 text-red-700', '이자 대상': 'bg-red-100 text-red-800', 정산부족분: 'bg-orange-50 text-orange-700',
  '소속 다름': 'bg-violet-50 text-violet-700', '소속 없음': 'bg-amber-100 text-amber-900', 조정: 'bg-sky-50 text-sky-700',
  '25일': 'bg-amber-50 text-amber-800', 사업소득: 'bg-emerald-50 text-emerald-700', '2대이상': 'bg-indigo-50 text-indigo-700',
  예비기사: 'bg-slate-100 text-slate-700', '예비기사 있음': 'bg-slate-100 text-slate-700', '법인 여럿': 'bg-indigo-50 text-indigo-700',
  '기사앱 분배': 'bg-teal-50 text-teal-700',
};
/** 칩 필터 — 엑셀의 시트(구분)와 색칠(이슈)을 한 줄로. */
const FILTERS: { key: string; label: string; test: (p: ToplinePayee) => boolean }[] = [
  { key: 'all', label: '전체', test: () => true },
  ...(['지입기본', '사업소득', '2대이상', '직영', '예비기사', '25일'] as ToplineBucket[]).map((b) => ({ key: b, label: BUCKET_LABEL[b], test: (p: ToplinePayee) => p.bucket === b })),
  { key: 'minus', label: '마이너스', test: (p) => p.newCarry < 0 || p.tags.includes('마이너스') },
  { key: 'carry', label: '정산부족분', test: (p) => p.carry < 0 },
  { key: 'interest', label: '이자 대상', test: (p) => p.tags.includes('이자 대상') },
  { key: 'diff', label: '소속 다름', test: (p) => p.tags.includes('소속 다름') },
  { key: 'nogrp', label: '소속 없음', test: (p) => p.payGrpId === null },
  { key: 'adj', label: '조정', test: (p) => p.adjusts.length > 0 },
  { key: 'split', label: '기사앱 분배', test: (p) => p.tags.includes('기사앱 분배') },
];

export default function ToplineTab() {
  const fitRef = useRef<HTMLDivElement>(null);
  const fitH = useFitHeight(fitRef);
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const [months, setMonths] = useState<string[]>([]);
  const [mon, setMon] = useSticky('acct.topline.mon', prevMonth());
  const [data, setData] = useState<Topline | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [filter, setFilter] = useSticky('acct.topline.filter', 'all');
  const [grp, setGrp] = useSticky<string>('acct.topline.grp', '');
  const [q, setQ] = useSticky('acct.topline.q', '');
  const [open, setOpen] = useState<string | null>(null);
  const [setting, setSetting] = useState<ToplinePayee | null>(null);
  const [adjusting, setAdjusting] = useState<ToplinePayee | null>(null);
  // #290 이현숙 — 줄을 두 번 누르면 그 달 기사 명세서(받는 분이 차주면 그 차를 운행한 기사마다 탭).
  const [slipOf, setSlipOf] = useState<ToplinePayee | null>(null);
  const [showPost, setShowPost] = useState(false);
  const [showExcl, setShowExcl] = useState(false);

  useEffect(() => { api.listDriveMonths().then((r) => setMonths(r.rows)).catch(() => setMonths([])); }, []);
  const load = () => {
    setError('');
    setBusy('불러오는 중…');
    api.getTopline({ driveMon: mon }).then(setData).catch((e) => { setData(null); setError(errMsg(e)); }).finally(() => setBusy(''));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [mon]);

  const grpName = useMemo(() => {
    const m = new Map<number, string>();
    for (const g of data?.grps ?? []) m.set(g.grp_id, g.grp_name);
    for (const l of data?.lines ?? []) if (l.grpId && l.grpName) m.set(l.grpId, l.grpName);
    return (id: number | null) => (id === null ? '소속 없음' : m.get(id) ?? `소속 ${id}`);
  }, [data]);

  const summary = useMemo(() => {
    const rows = new Map<string, Record<string, number>>();
    for (const p of data?.payees ?? []) {
      const k = grpName(p.payGrpId);
      const r = rows.get(k) ?? {};
      r[p.bucket] = (r[p.bucket] ?? 0) + p.pay;
      rows.set(k, r);
    }
    return [...rows.entries()].sort((a, b) => a[0].localeCompare(b[0], 'ko'));
  }, [data, grpName]);

  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) ?? FILTERS[0]!;
    // #419 띄어쓰기 낱말 중 하나라도(OR) · 대소문자는 예전처럼 구분 · 칸 안 띄어쓰기는 무시.
    const words = searchWords(q, false);
    return (data?.payees ?? []).filter((p) => f.test(p)
      && (grp === '' || grpName(p.payGrpId) === grp)
      && hitAny(words, [`${p.name}${p.ownerName ?? ''}${p.cars.join('')}`.replace(/\s/g, '')], false));
  }, [data, filter, grp, q, grpName]);

  const excluded = (data?.lines ?? []).filter((l) => l.excluded);
  const closed = !!data?.closed;
  const preview = !closed && !!data?.preview;
  // 확정한 달도 설정·조정은 열어 둔다(10-23) — 굳힌 숫자는 그대로이고 바뀐 묶음에 「재확정」이 뜬다.
  const editable = canWrite;
  const groupOf = (p: ToplinePayee) => (p.key.startsWith('d:') ? p.key : `o:${p.ownerId}`);
  const changeOf = new Map((data?.changes ?? []).map((c) => [c.group, c]));

  async function doClose(reopen: boolean) {
    const msg = reopen
      ? `${mon} 확정을 풀까요? 재무추가에 넣은 정산부족분 줄을 내리고 다시 계산된 값으로 돌아갑니다.`
      : `${mon} 을 확정할까요? 지금 계산을 굳히고 정산부족분 ${data?.postings.length ?? 0}줄을 재무추가(정산월 없음)에 넣습니다.`;
    if (!window.confirm(msg)) return;
    setBusy(reopen ? '확정 푸는 중…' : '확정하는 중…');
    try { await api.closeTopline({ driveMon: mon, reopen }); load(); } catch (e) { setError(errMsg(e)); setBusy(''); }
  }

  async function reconfirm(group: string) {
    const c = changeOf.get(group);
    if (!c) return;
    const msg = `${c.name} 을 지금 숫자로 재확정할까요?\n이체액 ${money(c.before)} → ${money(c.after)}`
      + (c.postingBefore !== c.postingAfter ? `\n정산부족분 반영 ${money(c.postingBefore)} → ${money(c.postingAfter)}(재무추가 줄도 바뀝니다)` : '');
    if (!window.confirm(msg)) return;
    setBusy('재확정하는 중…');
    try { await api.reconfirmTopline({ driveMon: mon, group }); load(); } catch (e) { setError(errMsg(e)); setBusy(''); }
  }

  const linesOf = (p: ToplinePayee) => (data?.lines ?? []).filter((l) => !l.excluded && (p.kind === 'owner'
    ? l.ownerId === p.ownerId && !/^직영/.test(l.driverKind ?? '')
    : l.driverId === p.id));

  async function download() {
    if (!data) return;
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();
    const sumRows = summary.map(([g, r]) => [g, ...SUMMARY_COLS.map((b) => r[b] ?? 0), SUMMARY_COLS.reduce((s, b) => s + (r[b] ?? 0), 0), r['25일'] ?? 0]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      [`${mon} 차인지급액 조정 — 법인별 합계`],
      ['소속', ...SUMMARY_COLS.map((b) => BUCKET_LABEL[b]), '15일 이체 합계', '25일 지급'], ...sumRows,
    ]), '총정리');
    const payRow = (p: ToplinePayee) => [grpName(p.payGrpId), BUCKET_LABEL[p.bucket], p.name, p.ownerName ?? '', p.cars.join(', '),
      p.net, p.carry, p.adjust, p.tax, p.pay, p.newCarry, p.bankOwner ?? '', p.bankName ?? '', p.bankNumber ?? '', p.tags.map(tagLabel).join(' · ')];
    const payHead = ['소속(지급)', '구분', '받는 분', '명의', '차량', '차인지급액', '이월 정산부족분', '조정', '3.3%', '이체액', '새 정산부족분', '예금주', '은행', '계좌번호', '표시'];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([payHead, ...data.payees.filter((p) => p.bucket !== '25일').map(payRow)]), '15일 이체');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([payHead, ...data.payees.filter((p) => p.bucket === '25일').map(payRow)]), '25일 지급');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['제외 사유', '소속', '명의', '기사', '차량', '차인지급액'],
      ...excluded.map((l) => [l.excluded, l.grpName ?? '', l.ownerName ?? '', l.driverName ?? '', l.carNo ?? '', l.net]),
    ]), '제외');
    XLSX.writeFile(wb, `차인지급액조정_${mon}.xlsx`);
  }

  return (
    <div className="space-y-1.5">
      <div className="card flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
        <label className="flex items-center gap-1">정산월
          <select className="input py-1" value={mon} onChange={(e) => setMon(e.target.value)}>
            {[...new Set([mon, ...months])].sort().reverse().map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <button className="btn-ghost" onClick={load} disabled={!!busy}>다시 계산</button>
        <button className="btn-ghost" onClick={() => void download()} disabled={!data}>엑셀 받기</button>
        {data && (closed
          ? <span className="rounded bg-emerald-50 px-2 py-0.5 text-emerald-700" title={`${dash(data.closed?.by)} · ${dash(data.closed?.at)}`}>
              확정됨 {data.closed?.at.slice(0, 16)}{data.closed?.posted != null ? ` · 정산부족분 ${data.closed.posted}줄 재무추가` : ' · 정산부족분 아직 안 넣음'}
            </span>
          : preview
            ? <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-900">미리보기 — 명세 생성 전</span>
            : <span className="text-ink-soft">명세 {data.statement.lines.toLocaleString()}줄 · 마지막 생성 {dash(data.statement.last?.slice(0, 16))}</span>)}
        {data && canWrite && (closed
          ? <span className="ml-auto flex gap-1">
              {data.closed?.posted == null && <button className="btn-primary" onClick={() => void doClose(false)} disabled={!!busy}>정산부족분 재무추가에 넣기</button>}
              <button className="btn-ghost" onClick={() => void doClose(true)} disabled={!!busy}>확정 풀기</button>
            </span>
          : <button className="btn-primary ml-auto" onClick={() => void doClose(false)} disabled={!!busy || !data.payees.length || preview}
                    title={preview ? '명세를 만든 뒤 확정할 수 있습니다' : undefined}>확정</button>)}
        {busy && <span className="text-ink-soft">{busy}</span>}
      </div>
      {error && <ErrorBox message={error} />}
      {preview && data && data.payees.length > 0 && (
        <div className="card border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {mon} 명세가 아직 없어 <b>명세를 만들 때와 같은 계산</b>으로 미리 보여 드립니다
          {data.preview?.targetMon && data.preview.targetMon !== mon ? ` (대상 = ${data.preview.targetMon} 정산현황에서 '정산' 체크한 분)` : ''}.
          설정·조정은 지금 해 두시면 명세를 만든 뒤에도 그대로 남습니다. 재무추가·월정기를 고치면 「다시 계산」으로 바로 반영되고, 확정은 명세를 만든 뒤에 합니다.
        </div>
      )}
      {closed && data && data.changes.length > 0 && (
        <div className="card border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          확정 뒤 숫자가 바뀐 분 {data.changes.length}명 — 「재확정」을 누르면 그분만 지금 숫자로 굳고 재무추가 정산부족분 줄도 바뀝니다.
          <div className="mt-1 flex flex-wrap gap-1">
            {data.changes.map((c) => (
              <span key={c.group} className="flex items-center gap-1 rounded border border-amber-300 bg-surface px-1.5 py-0.5 text-xs">
                {c.name} {money(c.before)} → {money(c.after)}
                {canWrite && <button className="btn-ghost px-1 py-0 text-xs" onClick={() => void reconfirm(c.group)} disabled={!!busy}>재확정</button>}
              </span>
            ))}
          </div>
        </div>
      )}
      {data && !data.payees.length && !busy && (
        <div className="card p-5 text-sm text-ink-soft">{mon} 에 계산할 정산 자료가 없습니다.</div>
      )}

      {data && data.payees.length > 0 && (
        <>
          <div className="card overflow-x-auto p-3">
            <div className="mb-1 text-sm font-semibold">법인별 합계 <span className="font-normal text-ink-soft">— 15일 이체 · 25일 지급은 따로</span></div>
            <table className="w-full min-w-[720px] text-sm">
              <thead><tr className="border-b border-line text-ink-soft">
                <th className="py-1 text-left font-medium">소속</th>
                {SUMMARY_COLS.map((b) => <th key={b} className="py-1 text-right font-medium">{BUCKET_LABEL[b]}</th>)}
                <th className="py-1 text-right font-semibold">15일 이체 합계</th>
                <th className="py-1 text-right font-medium">25일 지급</th>
              </tr></thead>
              <tbody>
                {summary.map(([g, r]) => (
                  <tr key={g} className={`border-b border-line/60 ${g === '소속 없음' ? 'bg-amber-50' : ''}`}>
                    <td className="py-1"><button className="hover:underline" onClick={() => setGrp(grp === g ? '' : g)}>{g}</button></td>
                    {SUMMARY_COLS.map((b) => <td key={b} className="py-1 text-right tabular-nums">{money(r[b] ?? 0)}</td>)}
                    <td className="py-1 text-right font-semibold tabular-nums">{money(SUMMARY_COLS.reduce((s, b) => s + (r[b] ?? 0), 0))}</td>
                    <td className="py-1 text-right tabular-nums">{money(r['25일'] ?? 0)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="py-1">합계</td>
                  {SUMMARY_COLS.map((b) => <td key={b} className="py-1 text-right tabular-nums">{money(summary.reduce((s, [, r]) => s + (r[b] ?? 0), 0))}</td>)}
                  <td className="py-1 text-right tabular-nums">{money(summary.reduce((s, [, r]) => s + SUMMARY_COLS.reduce((t, b) => t + (r[b] ?? 0), 0), 0))}</td>
                  <td className="py-1 text-right tabular-nums">{money(summary.reduce((s, [, r]) => s + (r['25일'] ?? 0), 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card space-y-1.5 px-3 py-2">
            <div className="flex flex-wrap items-center gap-1">
              {FILTERS.map((f) => {
                const cnt = data.payees.filter(f.test).length;
                if (f.key !== 'all' && !cnt) return null;
                return (
                  <button key={f.key} onClick={() => setFilter(f.key)}
                          className={`rounded-full border px-2.5 py-0.5 text-xs ${filter === f.key ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-line text-ink-soft hover:bg-canvas'}`}>
                    {f.label} <span className="tabular-nums">{cnt}</span>
                  </button>
                );
              })}
              <select className="input ml-auto py-0.5 text-xs" value={grp} onChange={(e) => setGrp(e.target.value)}>
                <option value="">전 소속</option>
                {summary.map(([g]) => <option key={g} value={g}>{g}</option>)}
              </select>
              <input className="input w-40 py-0.5 text-xs" placeholder="이름·차량 찾기" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <TopScrollbar target={fitRef} />
            <div ref={fitRef} className="overflow-auto" style={{ maxHeight: fitH }}>
              <table className="w-full min-w-[1100px] text-sm [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-surface">
                <thead><tr className="border-b border-line text-xs text-ink-soft">
                  <th className="py-1 text-left font-medium">소속(지급)</th>
                  <th className="py-1 text-left font-medium">구분</th>
                  <th className="py-1 text-left font-medium">받는 분</th>
                  <th className="py-1 text-left font-medium">명의</th>
                  <th className="py-1 text-left font-medium">차량</th>
                  <th className="py-1 text-right font-medium">차인지급액</th>
                  <th className="py-1 text-right font-medium" title="정산월 없는 재무추가 잔액 중 마이너스(플러스 잔액은 쓰지 않는다)">이월 정산부족분</th>
                  <th className="py-1 text-right font-medium">조정</th>
                  <th className="py-1 text-right font-medium">3.3%</th>
                  <th className="py-1 text-right font-semibold">이체액</th>
                  <th className="py-1 text-right font-medium">새 정산부족분</th>
                  <th className="py-1 text-left font-medium">계좌</th>
                  <th className="py-1" />
                </tr></thead>
                <tbody>
                  {shown.map((p) => (
                    <Fragment key={p.key}>
                      <tr className={`cursor-pointer border-b border-line/60 hover:bg-brand-50 ${p.newCarry < 0 ? 'bg-red-50/40' : ''}`}
                          onClick={() => setOpen(open === p.key ? null : p.key)}
                          onDoubleClick={() => { window.getSelection()?.removeAllRanges(); setSlipOf(p); }}
                          title="두 번 누르면 이 달 명세서">
                        <td className="py-1">{grpName(p.payGrpId)}</td>
                        <td className="py-1">
                          <span className="mr-1">{BUCKET_LABEL[p.bucket]}</span>
                          {p.tags.filter((t) => t !== p.bucket && !(t === '직영' && p.bucket === '직영')).map((t) => (
                            <span key={t} className={`mr-0.5 rounded px-1 text-[10px] ${TAG_STYLE[t] ?? 'bg-slate-100 text-slate-700'}`}>{tagLabel(t)}</span>
                          ))}
                        </td>
                        <td className="py-1 font-medium">{p.name}</td>
                        <td className="py-1 text-ink-soft">{p.kind === 'owner' || p.bucket === '예비기사' ? dash(p.ownerName) : ''}</td>
                        <td className="max-w-[140px] truncate py-1 text-xs text-ink-soft" title={p.cars.join(', ')}>{p.cars.join(', ')}</td>
                        <td className="py-1 text-right tabular-nums">{money(p.net)}</td>
                        <td className="py-1 text-right tabular-nums text-orange-700">{p.carry ? money(p.carry) : ''}</td>
                        <td className="py-1 text-right tabular-nums text-sky-700">{p.adjust ? money(p.adjust) : ''}</td>
                        <td className="py-1 text-right tabular-nums">{p.tax ? money(p.tax) : ''}</td>
                        <td className="py-1 text-right font-semibold tabular-nums">{money(p.pay)}</td>
                        <td className="py-1 text-right tabular-nums text-red-700">{p.newCarry ? money(p.newCarry) : ''}</td>
                        <td className="py-1 text-xs text-ink-soft">{[p.bankOwner, p.bankName, p.bankNumber].filter(Boolean).join(' ')}</td>
                        <td className="whitespace-nowrap py-1 text-right" onClick={(e) => e.stopPropagation()}>
                          {editable && p.bucket !== '직영' && <button className="btn-ghost px-1.5 py-0 text-xs" onClick={() => setSetting(p)}>설정</button>}
                          {editable && p.bucket === '직영' && <button className="btn-ghost px-1.5 py-0 text-xs" onClick={() => setSetting(p)}>소속</button>}
                          {editable && <button className="btn-ghost px-1.5 py-0 text-xs" onClick={() => setAdjusting(p)}>조정</button>}
                          {closed && canWrite && changeOf.has(groupOf(p)) && (
                            <button className="btn-ghost px-1.5 py-0 text-xs text-amber-800" title={`지금 계산 ${money(changeOf.get(groupOf(p))!.after)}`}
                                    onClick={() => void reconfirm(groupOf(p))}>재확정</button>
                          )}
                        </td>
                      </tr>
                      {open === p.key && (
                        <tr className="bg-canvas/60"><td colSpan={13} className="px-3 py-2">
                          <table className="w-full text-xs">
                            <thead><tr className="text-ink-soft">
                              <th className="text-left font-medium">편성 소속</th><th className="text-left font-medium">차량</th><th className="text-left font-medium">기사</th>
                              <th className="text-right font-medium">차인지급액</th><th className="text-right font-medium">예비기사급여</th>
                              <th className="text-right font-medium">이월 잔액</th><th className="text-right font-medium">쓴 부족분</th><th className="text-left font-medium">편성번호</th>
                            </tr></thead>
                            <tbody>
                              {linesOf(p).map((l, i) => (
                                <tr key={i}>
                                  <td>{dash(l.grpName)}</td><td>{dash(l.carNo)}</td><td>{dash(l.driverName)}</td>
                                  <td className="text-right tabular-nums">{money(l.net)}</td>
                                  <td className="text-right tabular-nums">{l.reserve ? money(l.reserve) : ''}</td>
                                  <td className="text-right tabular-nums">{l.carry ? money(l.carry) : ''}</td>
                                  <td className="text-right tabular-nums">{l.carryUsed ? money(l.carryUsed) : ''}</td>
                                  <td>{dash(l.carAllocId)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {p.adjusts.map((a) => <div key={a.id} className="mt-1 text-xs text-sky-800">조정 {money(a.amount)} — {a.reason}</div>)}
                        </td></tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
                <tfoot><tr className="font-semibold">
                  <td className="py-1" colSpan={5}>{shown.length}명</td>
                  <td className="py-1 text-right tabular-nums">{money(shown.reduce((s, p) => s + p.net, 0))}</td>
                  <td className="py-1 text-right tabular-nums">{money(shown.reduce((s, p) => s + p.carry, 0))}</td>
                  <td className="py-1 text-right tabular-nums">{money(shown.reduce((s, p) => s + p.adjust, 0))}</td>
                  <td className="py-1 text-right tabular-nums">{money(shown.reduce((s, p) => s + p.tax, 0))}</td>
                  <td className="py-1 text-right tabular-nums">{money(shown.reduce((s, p) => s + p.pay, 0))}</td>
                  <td className="py-1 text-right tabular-nums">{money(shown.reduce((s, p) => s + p.newCarry, 0))}</td>
                  <td colSpan={2} />
                </tr></tfoot>
              </table>
            </div>
          </div>

          <div className="card p-3 text-sm">
            <button className="font-semibold hover:underline" onClick={() => setShowPost(!showPost)}>
              정산부족분 반영 {data.postings.length}줄 {showPost ? '▲' : '▼'}
            </button>
            <span className="ml-2 text-xs text-ink-soft">
              {closed && data.closed?.posted != null
                ? `${data.closed.postedAt?.slice(0, 16) ?? ''} 확정 때 재무추가(정산월 없음)에 ${data.closed.posted}줄 넣었습니다 — 새 부족분은 공제, 갚은 부족분은 회수(지급).`
                : `확정하면 이 ${data.postings.length}줄이 재무추가(정산월 없음)에 들어갑니다 — 새 부족분은 공제, 갚은 부족분은 회수(지급).`}
            </span>
            {data.closed?.note && <div className="mt-1 rounded bg-red-50 px-2 py-1 text-xs text-red-700">{data.closed.note}</div>}
            {showPost && (
              <table className="mt-2 w-full text-xs">
                <thead><tr className="text-ink-soft">
                  <th className="text-left font-medium">편성번호</th><th className="text-left font-medium">명의</th><th className="text-left font-medium">차량</th>
                  <th className="text-right font-medium">이전 부족분</th><th className="text-right font-medium">이후 부족분</th><th className="text-right font-medium">반영</th>
                </tr></thead>
                <tbody>
                  {data.postings.map((x, i) => (
                    <tr key={i} className="border-b border-line/40">
                      <td>{dash(x.carAllocId)}</td><td>{dash(x.ownerName)}</td><td>{dash(x.carNo)}</td>
                      <td className="text-right tabular-nums">{money(x.before)}</td><td className="text-right tabular-nums">{money(x.after)}</td>
                      <td className={`text-right tabular-nums ${x.delta > 0 ? 'text-emerald-700' : 'text-red-700'}`}>{x.delta > 0 ? `회수 +${money(x.delta)}` : `공제 ${money(x.delta)}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card p-3 text-sm">
            <button className="font-semibold hover:underline" onClick={() => setShowExcl(!showExcl)}>
              정산 대상에서 뺀 줄 {excluded.length}줄 {showExcl ? '▲' : '▼'}
            </button>
            <span className="ml-2 text-xs text-ink-soft">
              {Object.entries(excluded.reduce<Record<string, number>>((a, l) => ({ ...a, [l.excluded!]: (a[l.excluded!] ?? 0) + 1 }), {})).map(([k, v]) => `${k} ${v}`).join(' · ')}
            </span>
            {showExcl && (
              <table className="mt-2 w-full text-xs">
                <tbody>
                  {excluded.map((l, i) => (
                    <tr key={i} className="border-b border-line/40">
                      <td>{l.excluded}</td><td>{dash(l.grpName)}</td><td>{dash(l.ownerName)}</td><td>{dash(l.driverName)}</td><td>{dash(l.carNo)}</td>
                      <td className="text-right tabular-nums">{money(l.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {setting && data && (
        <Modal>
          <SettingForm p={setting} grps={data.grps} grpName={grpName} lines={linesOf(setting)}
                       onCancel={() => setSetting(null)} onSaved={() => { setSetting(null); load(); }} />
        </Modal>
      )}
      {slipOf && (
        <Modal width="max-w-6xl">
          <SlipView p={slipOf} mon={mon} preview={!!data?.preview} drivers={[...new Map(linesOf(slipOf).filter((l) => l.driverId != null)
            .map((l) => [l.driverId!, l] as const)).values()]} onClose={() => setSlipOf(null)} />
        </Modal>
      )}
      {adjusting && (
        <Modal>
          <AdjustForm p={adjusting} mon={mon} onCancel={() => setAdjusting(null)} onSaved={() => { setAdjusting(null); load(); }} />
        </Modal>
      )}
    </div>
  );
}

/** 설정 — 관리기사B 명의는 25일 지급·사업소득, 기사(예비기사·관리기사A)는 기사 소속·소득 구분. 다음 달에도 그대로 간다. */
function SettingForm({ p, grps, grpName, lines, onCancel, onSaved }: {
  p: ToplinePayee; grps: { grp_id: number; grp_name: string }[]; grpName: (id: number | null) => string;
  lines: { grpId: number | null }[]; onCancel: () => void; onSaved: () => void;
}) {
  const owner = p.kind === 'owner';
  const [day25, setDay25] = useState(p.bucket === '25일');
  const [biz, setBiz] = useState(p.tags.includes('사업소득'));
  const allocGrp = lines.find((l) => l.grpId !== null)?.grpId ?? null;
  const [payGrp, setPayGrp] = useState<string>(p.payGrpId !== null && p.payGrpId !== allocGrp ? String(p.payGrpId) : '');
  const [income, setIncome] = useState<string>(p.tags.includes('3.3%') ? '3.3%' : '4대보험');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setErr('');
    try {
      if (owner) await api.saveToplineOwner({ ownerId: p.ownerId, payDay25: day25, bizIncome: biz });
      else await api.saveToplineDriver({ driverId: p.id, payGrpId: payGrp ? Number(payGrp) : null, incomeKind: p.bucket === '예비기사' ? (income as '4대보험' | '3.3%') : undefined });
      onSaved();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="card w-[420px] max-w-full space-y-3 p-5 text-sm">
      <h2 className="text-lg font-bold">{p.name} 설정</h2>
      <p className="text-xs text-ink-soft">여기서 정한 값은 다음 달에도 그대로 씁니다.</p>
      {owner ? (
        <>
          <label className="flex items-center gap-2"><input type="checkbox" checked={day25} onChange={(e) => setDay25(e.target.checked)} />
            25일 지급 — 사업자 정산(세금계산서)</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={biz} onChange={(e) => setBiz(e.target.checked)} />
            사업소득 — 3.3% 떼고 지급</label>
        </>
      ) : (
        <>
          <label className="block">기사 소속(급여가 나가는 법인)
            <select className="input mt-1 w-full" value={payGrp} onChange={(e) => setPayGrp(e.target.value)}>
              <option value="">편성 소속 그대로 ({grpName(allocGrp)})</option>
              {grps.map((g) => <option key={g.grp_id} value={g.grp_id}>{g.grp_name}</option>)}
            </select>
          </label>
          {p.bucket === '예비기사' && (
            <label className="block">소득 구분
              <select className="input mt-1 w-full" value={income} onChange={(e) => setIncome(e.target.value)}>
                <option value="4대보험">4대보험(떼지 않음)</option>
                <option value="3.3%">3.3%(사업소득 — 떼고 지급)</option>
              </select>
            </label>
          )}
        </>
      )}
      {err && <ErrorBox message={err} />}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onCancel}>닫기</button>
        <button className="btn-primary" onClick={() => void save()} disabled={busy}>저장</button>
      </div>
    </div>
  );
}

/** 수기 조정 — 더하거나 빼고 사유를 남긴다. 받는 분 합계와 새 정산부족분이 같이 바뀐다. */
function AdjustForm({ p, mon, onCancel, onSaved }: { p: ToplinePayee; mon: string; onCancel: () => void; onSaved: () => void }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr('');
    try { await fn(); onSaved(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const add = () => run(() => api.saveToplineAdjust({ driveMon: mon, payeeKind: p.kind, payeeId: p.kind === 'owner' ? p.ownerId : p.id, amount: uncomma(amount), reason }));
  const remove = (id: number) => run(() => api.saveToplineAdjust({ driveMon: mon, id, amount: 0 }));

  return (
    <div className="card w-[440px] max-w-full space-y-3 p-5 text-sm">
      <h2 className="text-lg font-bold">{p.name} 조정 · {mon}</h2>
      <p className="text-xs text-ink-soft">
        지금 이체액 {money(p.pay)}원{p.newCarry < 0 ? ` · 새 정산부족분 ${money(p.newCarry)}원` : ''}. 빼는 금액은 앞에 − 를 붙입니다.
      </p>
      {p.adjusts.map((a) => (
        <div key={a.id} className="flex items-center justify-between rounded bg-canvas px-2 py-1 text-xs">
          <span>{money(a.amount)} — {a.reason}</span>
          {/* 음수 id = 기사앱에서 차주가 넣은 예비기사 분배 — 여기서 못 지운다(차주가 앱에서 고친다). */}
          {a.id > 0 ? <button className="text-red-700 hover:underline" onClick={() => void remove(a.id)} disabled={busy}>지우기</button> : <span className="text-ink-faint">기사앱 입력</span>}
        </div>
      ))}
      <label className="block">금액
        <input className="input mt-1 w-full text-right" inputMode="numeric" value={amount}
               onChange={(e) => setAmount(commaInput(e.target.value.replace(/^−/, '-')))} />
      </label>
      <label className="block">사유(필수)
        <input className="input mt-1 w-full" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="예: 본부장 지시 — 생활비 선지급" />
      </label>
      {err && <ErrorBox message={err} />}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onCancel}>닫기</button>
        <button className="btn-primary" onClick={() => void add()} disabled={busy || !uncomma(amount) || !reason.trim()}>더하기</button>
      </div>
    </div>
  );
}

/** #290 — 차인지급액 조정 줄의 그 달 명세서. 받는 분이 차주면 그 차를 운행한 기사마다 탭(명세는 기사 단위). */
function SlipView({ p, mon, preview, drivers, onClose }: { p: ToplinePayee; mon: string; preview: boolean; drivers: ToplineLine[]; onClose: () => void }) {
  const [pick, setPick] = useState<number | null>(null);
  const cur = drivers.find((l) => l.driverId === pick) ?? drivers[0];
  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <b>{p.name}</b><span className="text-sm text-ink-soft">{mon} 명세서</span>
        {drivers.length > 1 && drivers.map((l) => (
          <button key={l.driverId} onClick={() => setPick(l.driverId)}
                  className={`rounded-full px-3 py-0.5 text-sm ${l.driverId === cur?.driverId ? 'bg-brand-600 text-white' : 'bg-canvas text-ink-soft hover:bg-brand-50'}`}>
            {l.driverName ?? l.driverId}{l.carNo ? ` · ${l.carNo}` : ''}
          </button>
        ))}
        <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
      </div>
      {preview && <p className="text-sm text-amber-700">아직 이 달 명세를 만들기 전이라 명세서가 비어 있을 수 있습니다.</p>}
      {cur ? (
        <SalarySlip key={cur.driverId} onClose={onClose}
          row={{ drive_mon: mon, driver_id: cur.driverId, driver_name: cur.driverName, driver_kind: cur.driverKind,
                 kind: p.kind === 'owner' ? '차주' : '직영', owner_name: p.kind === 'owner' ? p.ownerName : null,
                 grp_name: cur.grpName, result_amt: null }} />
      ) : <p className="text-sm text-ink-soft">이 줄에 연결된 기사가 없습니다.</p>}
    </div>
  );
}
